import React, { useState, useEffect } from "react";
import { Accordion, AccordionSummary, AccordionDetails, Autocomplete, TextField, Checkbox } from "@mui/material";
import { ExpandMore } from "@mui/icons-material";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import DeleteIcon from "@Components/StickySidebarViewer/assets/DeleteIcon";
import ModalStateDialogComponent from "@CubeComponents/html/modal/ModalWithState";
import ButtonComponent from "@CubeComponents/form/ButtonComponent";
import RadioButton from "@CubeComponents/Mui_Form/RadioButton";
import CheckBoxComponent from "@CubeComponents/Mui_Form/CheckBox";
import { BUTTON_TYPES } from "@CubeComponents/constants/globalVariable";
import { useTranslation } from "@i18n";

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

/**
 * One row of the assignment table. `checklistId`/`levelIds` are exactly
 * `checklistAssignmentSchema` (reviews.schema.ts) — what actually goes over the wire;
 * `checklistName`/`levelNames` are display-only columns computed here.
 */
export interface ChecklistAssignmentRow {
  checklistId: string;
  levelIds?: string[];
  checklistName?: string;
  levelNames?: string[];
}

/** One attached file in the draft review being assembled. */
export interface ChecklistDraftResource {
  fileVersionId?: string;
  name?: string;
  checklistAssignments?: ChecklistAssignmentRow[];
}

/**
 * Minimal structural contract this modal needs from the draft review it is editing. Deliberately
 * loose: the draft is a FRONTEND-only object (CreateReviewModal's wizard state, FilesInReview's
 * add-files state), not a server response, and the modal only ever reads the reviewFlow's checklists
 * and levels plus the resource list.
 */
export interface ChecklistAssignmentReviewDraft {
  reviewFlow: {
    checklists?: { _id?: string; title?: string }[];
    reviewLevels?: { _id?: string; serial?: number }[];
  };
  resources?: ChecklistDraftResource[];
  checklistAssignments?: ChecklistAssignmentRow[];
}

interface AssignmentTableState {
  list: ChecklistAssignmentRow[];
  selected: string[];
  showAddRow: boolean;
}

interface ChecklistAssignmentModalProps<TDraft extends ChecklistAssignmentReviewDraft> {
  reviewData: TDraft;
  setReviewData: (data: TDraft) => void;
  onNext: (data: TDraft) => void;
  onBack: () => void;
}

/**
 * Generic over the draft shape so each caller keeps its own richer wizard-state type; the modal only
 * relies on {@link ChecklistAssignmentReviewDraft}.
 */
const ChecklistAssignmentModal = <TDraft extends ChecklistAssignmentReviewDraft>({
  reviewData,
  setReviewData,
  onNext,
  onBack,
}: ChecklistAssignmentModalProps<TDraft>) => {
  const { t } = useTranslation();

  const [applyToAllFiles, setApplyToAllFiles] = useState<boolean>(true);
  const [expandedFileId, setExpandedFileId] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState<boolean>(false);

  const [globalAssignments, setGlobalAssignments] = useState<AssignmentTableState>({
    list: [],
    selected: [],
    showAddRow: false,
  });

  const [fileAssignments, setFileAssignments] = useState<Record<string, AssignmentTableState>>({});

  // For add row form
  const [addingToFileId, setAddingToFileId] = useState<string | null>(null);
  const [selectedChecklistId, setSelectedChecklistId] = useState<string>("");
  const [selectedLevelIds, setSelectedLevelIds] = useState<string[]>([]);

  // Load assignments from reviewData
  useEffect(() => {
    // Load global assignments
    setGlobalAssignments({
      list: reviewData?.checklistAssignments || [],
      selected: [],
      showAddRow: false,
    });

    const perFile: Record<string, AssignmentTableState> = {};
    reviewData?.resources?.forEach((file) => {
      // KNOWN BUG, preserved verbatim: `handleSave` writes back only `{ checklistId, levelIds }` (the
      // wire shape), dropping the display-only `checklistName`/`levelNames`. So re-opening this modal
      // (Back -> Assign) shows the previously saved rows with blank Checklist and Level cells. Does the
      // server read them? No — it only consumes checklistId/levelIds. Fix: re-derive the two labels from
      // reviewFlow when loading, which is a behaviour change, so it is left alone.
      perFile[file.fileVersionId!] = {
        list: file.checklistAssignments || [],
        selected: [],
        showAddRow: false,
      };
    });
    setFileAssignments(perFile);

    if (!applyToAllFiles && !isSaving && reviewData?.resources?.length! > 0) {
      setExpandedFileId(reviewData.resources![0]!.fileVersionId!);
    }
  }, [reviewData?.resources, reviewData?.checklistAssignments, applyToAllFiles, isSaving]);

  function showAddRowGlobal() {
    if (globalAssignments.showAddRow && selectedChecklistId && selectedLevelIds.length > 0) {
      saveAddRow(true);
      return;
    }

    setGlobalAssignments((prev) => ({ ...prev, showAddRow: true }));
    setAddingToFileId("global");
    setSelectedChecklistId("");
    setSelectedLevelIds([]);
  }

  function showAddRowForFile(fileId: string) {
    if (fileAssignments[fileId]?.showAddRow && selectedChecklistId && selectedLevelIds.length > 0) {
      saveAddRow(true);
      return;
    }

    setFileAssignments((prev) => ({
      ...prev,
      [fileId]: { ...prev[fileId]!, showAddRow: true },
    }));
    setAddingToFileId(fileId);
    setSelectedChecklistId("");
    setSelectedLevelIds([]);
  }

  function saveAddRow(keepFormOpen = false, checklistId?: string, levelIds?: string[]) {
    const finalChecklistId = checklistId || selectedChecklistId;
    const finalLevelIds = levelIds || selectedLevelIds;

    if (!addingToFileId || !finalChecklistId || finalLevelIds.length === 0) return;

    // KNOWN BUG, preserved verbatim: `reviewFlow.checklists` / `reviewFlow.reviewLevels` are optional on
    // the draft (and on `reviewFlowDataResultSchema`) but are dereferenced without a guard, so opening
    // this modal for a reviewFlow with neither throws. Does the server read them? No — read path only.
    // Fix: guard, which would change the emit, so it is left alone.
    const checklist = reviewData.reviewFlow.checklists!.find((c) => c._id === finalChecklistId);
    const levels = reviewData.reviewFlow.reviewLevels!.filter((l) => finalLevelIds.includes(l._id!));

    if (!checklist || levels.length === 0) return;

    const newAssignment: ChecklistAssignmentRow = {
      checklistId: checklist._id!,
      checklistName: checklist.title,
      levelIds: levels.map((l) => l._id!),
      levelNames: levels.map((l) => `Level ${l.serial}`),
    };

    if (addingToFileId === "global") {
      setGlobalAssignments((prev) => {
        const updated = [...prev.list];
        const existingIndex = updated.findIndex((a) => a.checklistId === checklist._id);

        if (existingIndex >= 0) {
          updated[existingIndex] = newAssignment;
        } else {
          updated.push(newAssignment);
        }

        return {
          ...prev,
          list: updated,
          showAddRow: keepFormOpen,
        };
      });
    } else {
      setFileAssignments((prev) => {
        const current = prev[addingToFileId]!;
        const updated = [...current.list];
        const existingIndex = updated.findIndex((a) => a.checklistId === checklist._id);

        if (existingIndex >= 0) {
          updated[existingIndex] = newAssignment;
        } else {
          updated.push(newAssignment);
        }

        return {
          ...prev,
          [addingToFileId]: {
            ...current,
            list: updated,
            showAddRow: keepFormOpen,
          },
        };
      });
    }

    if (!keepFormOpen) {
      setAddingToFileId(null);
    }
    setSelectedChecklistId("");
    setSelectedLevelIds([]);
  }

  function toggleRowGlobal(checklistId: string) {
    setGlobalAssignments((prev) => {
      const updated = prev.selected.includes(checklistId)
        ? prev.selected.filter((id: string) => id !== checklistId)
        : [...prev.selected, checklistId];

      return { ...prev, selected: updated };
    });
  }

  function toggleRowForFile(fileId: string, checklistId: string) {
    setFileAssignments((prev) => {
      const current = prev[fileId]!.selected;
      const updated = current.includes(checklistId)
        ? current.filter((id: string) => id !== checklistId)
        : [...current, checklistId];

      return {
        ...prev,
        [fileId]: { ...prev[fileId]!, selected: updated },
      };
    });
  }

  function toggleAllGlobal() {
    setGlobalAssignments((prev) => {
      const allIds = prev.list.map((a) => a.checklistId);
      const allSelected = prev.selected.length === allIds.length;
      return { ...prev, selected: allSelected ? [] : allIds };
    });
  }

  function toggleAllForFile(fileId: string) {
    setFileAssignments((prev) => {
      const allIds = prev[fileId]!.list.map((a) => a.checklistId);
      const allSelected = prev[fileId]!.selected.length === allIds.length;

      return {
        ...prev,
        [fileId]: { ...prev[fileId]!, selected: allSelected ? [] : allIds },
      };
    });
  }

  function removeSelectedGlobal() {
    setGlobalAssignments((prev) => {
      const updated = prev.list.filter((a) => !prev.selected.includes(a.checklistId));
      return { ...prev, list: updated, selected: [] };
    });
  }

  function removeSelectedFromFile(fileId: string) {
    setFileAssignments((prev) => {
      const current = prev[fileId]!;
      const updated = current.list.filter((a) => !current.selected.includes(a.checklistId));

      return {
        ...prev,
        [fileId]: { ...prev[fileId]!, list: updated, selected: [] },
      };
    });
  }

  async function handleSave() {
    setIsSaving(true);

    // `resources` is optional on the draft but dereferenced unconditionally here; the flow only reaches
    // this modal after files have been picked. Erased assertions.
    let updatedFiles: NonNullable<TDraft["resources"]>;

    if (applyToAllFiles) {
      const assignments = globalAssignments.list.map((a) => ({
        checklistId: a.checklistId,
        levelIds: a.levelIds || [],
      }));

      updatedFiles = reviewData.resources!.map((file) => ({
        ...file,
        checklistAssignments: assignments,
      })) as NonNullable<TDraft["resources"]>;
    } else {
      updatedFiles = reviewData.resources!.map((file) => {
        const fileList = fileAssignments[file.fileVersionId!]?.list || [];
        const assignments = fileList.map((a) => ({
          checklistId: a.checklistId,
          levelIds: a.levelIds || [],
        }));

        return {
          ...file,
          checklistAssignments: assignments,
        };
      }) as NonNullable<TDraft["resources"]>;
    }

    const updated = {
      ...reviewData,
      resources: updatedFiles,
    } as TDraft;

    setReviewData(updated);
    onNext(updated);
  }

  function getLevelOptions() {
    return (reviewData?.reviewFlow?.reviewLevels || []).map((level) => ({
      ...level,
      serialLabel: `Level ${level.serial}`,
    }));
  }

  function renderAddRow() {
    const levelOptions = getLevelOptions();
    const checklists = reviewData.reviewFlow.checklists || [];
    const selectedChecklist = checklists.find((c) => c._id === selectedChecklistId) || null;
    const selectedLevels = levelOptions.filter((l) => selectedLevelIds.includes(l._id!));

    return (
      <tr className="bg-gray-50">
        <td></td>
        <td className="p-2">
          <Autocomplete
            options={checklists}
            getOptionLabel={(option) => option.title || ""}
            value={selectedChecklist}
            onChange={(event, newValue) => {
              const newChecklistId = newValue?._id || "";
              setSelectedChecklistId(newChecklistId);
              if (newChecklistId && selectedLevelIds.length > 0) {
                saveAddRow(false, newChecklistId, selectedLevelIds);
              }
            }}
            renderOption={(props, option) => {
              const isSelected = selectedChecklist?._id === option._id;
              return (
                <li {...props}>
                  <Checkbox icon={icon} checkedIcon={checkedIcon} className="me-2" checked={isSelected} />
                  {option.title}
                </li>
              );
            }}
            renderInput={(params) => (
              <TextField {...params} placeholder={t("Select Checklist")} variant="outlined" size="small" />
            )}
            isOptionEqualToValue={(option, value) => option._id === value._id}
          />
        </td>
        <td className="p-2">
          <Autocomplete
            multiple
            disableCloseOnSelect
            options={levelOptions}
            getOptionLabel={(option) => option.serialLabel || ""}
            value={selectedLevels}
            onChange={(event, newValue) => {
              const newLevelIds = newValue.map((v) => v._id!);
              setSelectedLevelIds(newLevelIds);
            }}
            onClose={() => {
              if (selectedChecklistId && selectedLevelIds.length > 0) {
                saveAddRow(false, selectedChecklistId, selectedLevelIds);
              }
            }}
            renderOption={(props, option, { selected }) => (
              <li {...props}>
                <Checkbox icon={icon} checkedIcon={checkedIcon} className="me-2" checked={selected} />
                {option.serialLabel}
              </li>
            )}
            renderInput={(params) => (
              <TextField {...params} placeholder={t("Select Level(s)")} variant="outlined" size="small" />
            )}
            isOptionEqualToValue={(option, value) => option._id === value._id}
          />
        </td>
      </tr>
    );
  }

  function renderGlobalTable() {
    const data = globalAssignments;

    return (
      <div className="w-full border border-[#E3EAF6] rounded-md overflow-hidden mt-2">
        <div className="flex gap-2 p-2 bg-[#F3F7FD] border-b border-[#E3EAF6]">
          <ButtonComponent
            startIcon={<AddRoundedIcon />}
            className="px-4 py-1.5 rounded-md bg-[#EAF2FF] border border-[#D0E2FF] hover:bg-[#DCE9FF] text-sm font-medium"
            onClick={showAddRowGlobal}
          >
            {t("Add")}
          </ButtonComponent>
          <ButtonComponent
            startIcon={<DeleteIcon size={16} className={""} />}
            className={`px-4 py-1.5 rounded-md text-sm font-medium ${
              data.selected.length > 0
                ? "bg-transparent text-[#6B7280] hover:text-[#374151]"
                : "bg-transparent text-gray-300 cursor-not-allowed"
            }`}
            disabled={data.selected.length === 0}
            onClick={removeSelectedGlobal}
          >
            {t("Remove")}
          </ButtonComponent>
        </div>

        <table className="w-full text-sm table-fixed">
          <colgroup>
            <col className="w-[3.125rem]" />
            <col className="w-[40%]" />
            <col className="w-[calc(60%-3.125rem)]" />
          </colgroup>
          <thead className="bg-[#F3F7FD] text-gray-700 text-xs uppercase">
            <tr>
              <th className="p-2 text-center">
                <CheckBoxComponent
                  checked={data.list.length > 0 && data.selected.length === data.list.length}
                  onChange={toggleAllGlobal}
                />
              </th>
              <th className="text-start p-2 font-semibold">{t("Checklist")}</th>
              <th className="text-start p-2 font-semibold">{t("Level")}</th>
            </tr>
          </thead>
          <tbody>
            {data.list.map((assignment) => (
              <tr key={assignment.checklistId} className="hover:bg-gray-50">
                <td className="text-center p-2">
                  <CheckBoxComponent
                    checked={data.selected.includes(assignment.checklistId)}
                    onChange={() => toggleRowGlobal(assignment.checklistId)}
                  />
                </td>
                <td className="p-2 truncate" title={assignment.checklistName}>
                  {assignment.checklistName}
                </td>
                <td className="p-2 truncate" title={assignment.levelNames?.join(", ")}>
                  {assignment.levelNames?.join(", ")}
                </td>
              </tr>
            ))}

            {data.showAddRow && renderAddRow()}
          </tbody>
        </table>

        <div className="text-xs text-gray-600 p-3 border-t border-gray-100">
          {data.list.length} {t("rows")}
        </div>
      </div>
    );
  }

  function renderFileTable(fileId: string) {
    const data: AssignmentTableState = fileAssignments[fileId] || { list: [], selected: [], showAddRow: false };

    return (
      <div className="w-full border border-[#E3EAF6] rounded-md overflow-hidden mt-2">
        <div className="flex gap-2 p-2 bg-[#F3F7FD] border-b border-[#E3EAF6]">
          <ButtonComponent
            startIcon={<AddRoundedIcon />}
            className="px-4 py-1.5 rounded-md bg-[#EAF2FF] border border-[#D0E2FF] hover:bg-[#DCE9FF] text-sm font-medium"
            onClick={() => showAddRowForFile(fileId)}
          >
            {t("Add")}
          </ButtonComponent>
          <ButtonComponent
            startIcon={<DeleteIcon size={16} className={""} />}
            className={`px-4 py-1.5 rounded-md text-sm font-medium ${
              data.selected.length > 0
                ? "bg-transparent text-[#6B7280] hover:text-[#374151]"
                : "bg-transparent text-gray-300 cursor-not-allowed"
            }`}
            disabled={data.selected.length === 0}
            onClick={() => removeSelectedFromFile(fileId)}
          >
            {t("Remove")}
          </ButtonComponent>
        </div>

        <table className="w-full text-sm table-fixed">
          <colgroup>
            <col className="w-[3.125rem]" />
            <col className="w-[40%]" />
            <col className="w-[calc(60%-3.125rem)]" />
          </colgroup>
          <thead className="bg-[#F3F7FD] text-gray-700 text-xs uppercase">
            <tr>
              <th className="p-2 text-center">
                <CheckBoxComponent
                  checked={data.list.length > 0 && data.selected.length === data.list.length}
                  onChange={() => toggleAllForFile(fileId)}
                />
              </th>
              <th className="text-start p-2 font-semibold">{t("Checklist")}</th>
              <th className="text-start p-2 font-semibold">{t("Level")}</th>
            </tr>
          </thead>
          <tbody>
            {data.list.map((assignment) => (
              <tr key={assignment.checklistId} className="hover:bg-gray-50">
                <td className="text-center p-2">
                  <CheckBoxComponent
                    checked={data.selected.includes(assignment.checklistId)}
                    onChange={() => toggleRowForFile(fileId, assignment.checklistId)}
                  />
                </td>
                <td className="p-2 truncate" title={assignment.checklistName}>
                  {assignment.checklistName}
                </td>
                <td className="p-2 truncate" title={assignment.levelNames?.join(", ")}>
                  {assignment.levelNames?.join(", ")}
                </td>
              </tr>
            ))}

            {data.showAddRow && renderAddRow()}
          </tbody>
        </table>

        {/* Footer */}
        <div className="text-xs text-gray-600 p-3 border-t border-gray-100">
          {data.list.length} {t("rows")}
        </div>
      </div>
    );
  }

  return (
    <ModalStateDialogComponent
      isVisible
      allowModalPadding={false}
      width="100%"
      headerTitle={
        <div className="flex items-center gap-2 text-[#1A1A1A] font-semibold">
          <ArrowBackIosNewIcon onClick={onBack} className="text-lg cursor-pointer text-[#4B5563]" />
          {t("Assign Checklist")}
        </div>
      }
      modelContent={
        <div className="flex flex-col max-h-[75vh] overflow-y-auto p-5">
          <div className="mb-4 text-sm flex flex-col space-y-3">
            <RadioButton
              name="mode"
              value="all"
              label={t("Assign same Checklists to all files")}
              checked={applyToAllFiles}
              onChange={() => setApplyToAllFiles(true)}
            />
            <RadioButton
              name="mode"
              value="individual"
              label={t("Assign different Checklists to each file")}
              checked={!applyToAllFiles}
              onChange={() => setApplyToAllFiles(false)}
            />
          </div>

          {/* Tables */}
          {applyToAllFiles ? (
            <div>{renderGlobalTable()}</div>
          ) : (
            reviewData.resources?.map((file) => (
              <Accordion
                key={file.fileVersionId}
                expanded={expandedFileId === file.fileVersionId}
                onChange={(e, expanded: boolean) => setExpandedFileId(expanded ? file.fileVersionId! : null)}
                className="border border-[#E3EAF6] rounded-lg bg-white shadow-none mb-2 before:hidden"
              >
                <AccordionSummary
                  expandIcon={<ExpandMore className="text-xl" />}
                  className="bg-white border-b border-[#E3EAF6] px-4 py-2"
                >
                  <span className="font-medium text-gray-800">{file.name}</span>
                </AccordionSummary>
                <AccordionDetails className="p-0">{renderFileTable(file.fileVersionId!)}</AccordionDetails>
              </Accordion>
            ))
          )}
        </div>
      }
      footerContent={
        <div className="flex gap-2 justify-end">
          <ButtonComponent buttonType={BUTTON_TYPES.GRADIENT_BUTTON} onClick={handleSave} loading={isSaving}>
            {t("Assign")}
          </ButtonComponent>
          <ButtonComponent buttonType={BUTTON_TYPES.BORDER_BUTTON} onClick={onBack}>
            {t("Back")}
          </ButtonComponent>
        </div>
      }
      onCancel={onBack}
    />
  );
};

export default ChecklistAssignmentModal;
